import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/joy';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { APIMarkVoiceListened } from '../../api';
import useChatMedia from '../../hooks/useChatMedia';
import { openChatAttachment } from '../../utils/chatMedia';

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, '0')}`;
};

export default function VoiceMessage({ attachment, chatId, messageId, isYou }) {
  // Токен здесь не нужен: в кабинете клиента его подставляет перехватчик axios.
  const audioRef = useRef(null);
  const markedRef = useRef(false);

  const [playing, setPlaying]   = useState(false);
  const [cur, setCur]           = useState(0);
  const [dur, setDur]           = useState(Number(attachment?.duration) || 0);
  const [listened, setListened] = useState(!!attachment?.is_listened);
  const [failed, setFailed]     = useState(false);   // не удалось декодировать (напр. webm/opus на iOS)

  const bars = (attachment?.waveform && attachment.waveform.length)
    ? attachment.waveform
    : new Array(40).fill(30);
  const media = useChatMedia(attachment, chatId, messageId);
  const src = media.url;
  const mime = attachment?.mime_type || '';   // тип с сервера (Этап 2) — подсказка декодеру

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime   = () => setCur(a.currentTime);
    const onLoaded = () => { if (a.duration && isFinite(a.duration)) setDur(a.duration); };
    const onEnd    = () => { setPlaying(false); setCur(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const markListened = () => {
    if (markedRef.current || listened || isYou) return;
    markedRef.current = true;
    setListened(true);
    if (chatId && messageId) APIMarkVoiceListened(chatId, messageId);
  };

  const download = async () => {
    try { await openChatAttachment(attachment, chatId, messageId); }
    catch (_) { setFailed(true); }
  };
  useEffect(() => {
    setFailed(false); setPlaying(false);
    if (audioRef.current) audioRef.current.load();
  }, [src, media.version]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      // Пауза остальных голосовых (как в мессенджерах)
      document.querySelectorAll('audio').forEach((el) => { if (el !== a) el.pause(); });
      a.play().then(() => { setPlaying(true); markListened(); }).catch(() => setFailed(true));
    }
  };

  const progress = dur > 0 ? Math.min(1, cur / dur) : 0;

  const seek = (i) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const t = (i / bars.length) * dur;
    a.currentTime = t;
    setCur(t);
  };

  const accent = isYou ? 'rgba(255,255,255,0.95)' : 'var(--joy-palette-primary-solidBg)';
  const dim    = isYou ? 'rgba(255,255,255,0.4)'  : 'var(--joy-palette-neutral-300)';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, maxWidth: 280, mt: 1 }}>
      <audio crossOrigin="anonymous" referrerPolicy="no-referrer" ref={audioRef} preload="metadata" onError={() => { media.onError(); setFailed(true); }}>
        {mime ? <source src={src} type={mime} /> : null}
        <source src={src} />
      </audio>

      <IconButton
        size="sm"
        variant={isYou ? 'soft' : 'solid'}
        color={isYou ? 'neutral' : 'primary'}
        onClick={failed ? download : toggle}
        title={failed ? 'Формат не поддерживается — скачать' : (playing ? 'Пауза' : 'Воспроизвести')}
        sx={{ borderRadius: '50%', flexShrink: 0 }}
      >
        {failed ? <DownloadRoundedIcon /> : (playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />)}
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Волна с прогрессом (клик = перемотка) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 28, cursor: 'pointer', overflow: 'hidden' }}>
          {bars.map((v, i) => {
            const played = (i / bars.length) <= progress;
            return (
              <Box
                key={i}
                onClick={() => seek(i)}
                sx={{
                  width: 3, borderRadius: 2, flexShrink: 0,
                  height: `${Math.max(12, Math.min(100, v))}%`,
                  alignSelf: 'center',
                  bgcolor: played ? accent : dim,
                  transition: 'background-color .1s',
                }}
              />
            );
          })}
        </Box>

        {/* Тайминг + точка «не прослушано» */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
          <Typography
            level="body-xs"
            sx={{
              color: isYou ? 'rgba(255,255,255,0.85)' : 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {media.loading ? 'Обновляем ссылку…' : failed ? 'Не удалось воспроизвести' : fmt((playing || cur) ? cur : dur)}
          </Typography>
          {!isYou && !listened && !failed && (
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.solidBg' }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}